import { alpha, Theme } from '@mui/material';

/**
 * Neobet-specific CSS overrides for the third-party add-to-homescreen overlay.
 *
 * Targets `.adhs-*` class names injected by the philfung/add-to-homescreen package.
 * Applied via GlobalStyles inside InstallPromptProvider only when skin === neobet.
 *
 * Key overrides:
 * - Dark modal background matching neutral.700
 * - Primary-colored install buttons
 * - Yellow-tinted bouncing arrow (filter)
 * - "Install later" info box at the bottom
 */
export const getInstallPromptOverlayStyles = (theme: Theme) => ({
  '.adhs-container': {
    backgroundColor: `${alpha(theme.palette.common.black, 0.85)} !important`,
    color: `${theme.palette.text.primary} !important`,
  },
  '.adhs-container.visible': {
    backgroundColor: `${alpha(theme.palette.common.black, 0.9)} !important`,
  },
  '.adhs-modal': {
    backgroundColor: `${(theme.palette as unknown as Record<string, Record<string, string>>).neutral?.[700] || '#1e1e2f'} !important`,
    borderRadius: '12px !important',
    border: `1px solid ${theme.palette.neutral[500]} !important`,
    color: `${theme.palette.text.primary} !important`,
    fontFamily: `${theme.typography.subtitle1.fontFamily} !important`,
    boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.4)} !important`,
    padding: '24px !important',
  },
  '.adhs-logo': {
    marginTop: '-32px !important',
    width: 'fit-content !important',
    '& img': {
      height: '60px !important',
      width: 'auto !important',
    },
  },
  '.adhs-container.adhs-mobile .adhs-logo': {
    marginTop: '-16px !important',
  },
  '.adhs-title': {
    color: `${theme.palette.text.primary} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  '.adhs-error-title': {
    color: `${theme.palette.text.primary} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  '.adhs-error-body': {
    color: `${theme.palette.text.secondary} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  '.adhs-error-copy-link-button': {
    backgroundColor: `${alpha(theme.palette.common.white, 0.1)} !important`,
    color: `${theme.palette.text.primary} !important`,
    borderColor: `${alpha(theme.palette.common.white, 0.2)} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  '.adhs-install-app': {
    color: `${theme.palette.text.primary} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  '.adhs-app-name': {
    color: `${theme.palette.text.secondary} !important`,
  },
  '.adhs-app-url': {
    color: `${theme.palette.text.secondary} !important`,
  },
  '.adhs-instruction': {
    color: `${theme.palette.text.primary} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  '.adhs-emphasis': {
    color: `${theme.palette.primary.main} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  '.adhs-blurb': {
    color: `${theme.palette.text.secondary} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
    textAlign: 'center !important',
  },
  '.adhs-list-item': {
    color: `${theme.palette.text.primary} !important`,
  },
  '.adhs-button': {
    borderRadius: '8px !important',
    fontFamily: `${theme.typography.fontFamily} !important`,
    fontWeight: '700 !important',
  },
  '.adhs-button-install': {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText} !important`,
  },
  '.adhs-button-cancel': {
    backgroundColor: `${alpha(theme.palette.common.white, 0.1)} !important`,
    color: `${theme.palette.text.primary} !important`,
  },
  '.adhs-list-button': {
    backgroundColor: `${alpha(theme.palette.common.white, 0.06)} !important`,
    borderRadius: '8px !important',
    color: `${theme.palette.text.primary} !important`,
    boxShadow: 'none !important',
  },
  '.adhs-list-button-image-right': {
    filter: 'invert(1) !important',
  },
  '.adhs-number-container .adhs-circle': {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText} !important`,
  },
  '.adhs-number': {
    color: `${theme.palette.primary.contrastText} !important`,
    fontWeight: '700 !important',
  },
  '[class*="adhs-ios-safari"], [class*="adhs-ios-chrome"], [class*="adhs-android-chrome"], [class*="adhs-more-button"]':
    {
      backgroundColor: `${alpha(theme.palette.common.white, 0.06)} !important`,
      color: `${theme.palette.text.primary} !important`,
      boxShadow: 'none !important',
    },
  '.adhs-inappbrowser-openinsystembrowser .adhs-emphasis, .adhs-inappbrowser-openinsafari .adhs-emphasis': {
    backgroundColor: `${alpha(theme.palette.common.white, 0.06)} !important`,
    color: `${theme.palette.text.primary} !important`,
    boxShadow: 'none !important',
  },
  '[class*="bouncing-arrow-container"] img': {
    height: '32px !important',
    opacity: '0.9 !important',
    filter: 'sepia(1) hue-rotate(0deg) saturate(4) brightness(1.5)',
  },
  '.adhs-install-later-info': {
    marginTop: '16px !important',
    padding: '10px 12px !important',
    borderRadius: '8px !important',
    backgroundColor: `${alpha(theme.palette.common.white, 0.06)} !important`,
    color: `${theme.palette.text.secondary} !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
    fontSize: '13px !important',
    textAlign: 'center !important',
  },
});
